import {createSelector} from 'reselect';

import type {ApplicationStoreType} from 'constants/flow/flowTypes';
import type {TotalsType, QuoteItemType, CartItemType} from 'constants/flow/flowTypesCart';
import {PRODUCT_TYPES} from 'constants/product';
import {isHtoEnabled} from 'constants/flags/flags';

import {isProperty, getProperty} from 'libs/utils/utils';
import {MODULE_KEY} from 'pages/cart/constants';

/**
 * Returns cart key from store
 * @param {ApplicationStoreType} store - Current store
 * @returns {string} - Stored cartKey
 */
export const getCartKey = (store: ApplicationStoreType): string =>
    store[MODULE_KEY]?.quoteInfo.shareId || '';

/**
 * Get quote items
 * @param {ApplicationStoreType} store - Current store
 * @returns {Array<QuoteItemType>} - Products on cart
 */
export const getQuoteItems = (store: ApplicationStoreType): Array<QuoteItemType> =>
    store.quoteItems.quoteItems;

/**
 * Get true if in quoteItem there are products
 * @returns {boolean} - quoteItem is empty or not
 */
export const isNotEmptyQuoteItems = createSelector(getQuoteItems, quoteItems =>
    Boolean(quoteItems.length),
);

/**
 * Get quote id
 * @param {ApplicationStoreType} store - Current store
 * @returns {number} - quote id
 */
export const getQuoteId = (store: ApplicationStoreType): number => store.quoteItems.id;

/**
 * Get cart totals from store
 * @param {ApplicationStoreType} store - Current store
 * @returns {TotalsType} - Products totals for cart
 */
export const getCartTotals = (store: ApplicationStoreType): TotalsType => store.quoteItems.totals;

/**
 * Get cart discount from store
 * @param {ApplicationStoreType} store - Current store
 * @returns {string} - Discount on the Cart
 */
export const getDiscount = (store: ApplicationStoreType): string =>
    store.quoteItems.totals.discount;

/**
 * Does cart contain any contact lenses?
 * @param {ApplicationStoreType} store - Application store
 * @returns {boolean} flag "hasCartContactLenses"
 */
export const hasCartContactLenses = createSelector(
    getQuoteItems,
    (quoteItems: Array<QuoteItemType>): boolean =>
        Boolean(
            quoteItems.some(({type, active}) => type === PRODUCT_TYPES.CONTACT_LENSES && active),
        ),
);

/**
 * Get product added to the cart.
 *
 * @param {ApplicationStoreType} store - Application store.
 * @returns {boolean} - True if quoteItems store fulfilled.
 */
export const getIsQuoteItemsFulfilled = isProperty('quoteItems.status', 'fulfilled');

/**
 * Get applied coupons
 *
 * @param {ApplicationStoreType} store - application store
 * @returns {Array<string>} - List of applied coupons.
 */
export const getAppliedCoupons = getProperty('quoteItems.totals.appliedCoupons');

/**
 * Get all the items by field active
 * @param {ApplicationStoreType} store - Application store
 * @return {Array<CartItemType>} return active items
 */
export const getActiveCartItems = createSelector(getQuoteItems, (items: Array<CartItemType>) =>
    isHtoEnabled() ? items.filter(({active}) => active) : items,
);

/**
 * Get Added to cart Item by id
 * @param {ApplicationStoreType} store - Application store
 * @param {number} itemId - cart quote item id to get
 * @return {CartItemType|undefined} return active accessories
 */
export const getActiveCartItemById = createSelector(
    [getActiveCartItems, (store, itemId) => itemId],
    (items: Array<CartItemType>, itemId: number) =>
        items.find(({product_id: productId}) => productId === itemId),
);

/**
 * Get all the items by field active and count them, contact lenses is considered to be one item
 * @param {ApplicationStoreType} store - Application store
 * @return {Array<CartItemType>} return active items
 */
export const getActiveCartItemsCount = createSelector(
    getActiveCartItems,
    (items: Array<CartItemType>) =>
        items.reduce(
            (sum, {qty, type}) => sum + (type === PRODUCT_TYPES.CONTACT_LENSES ? 1 : Number(qty)),
            0,
        ),
);

/**
 * Get length filtered items by active:false flag
 * @param {ApplicationStoreType} store - Application store
 * @return {Number} return length items inactive
 */
export const getLengthInactiveItems = createSelector(
    getQuoteItems,
    (items: Array<CartItemType>) => items.filter(({active}) => !active).length,
);

/**
 * Determines if cart is loading
 *
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} true if cart is loading
 */
export const isCartLoading = (store: ApplicationStoreType): boolean =>
    (store.quoteItems.pending && !store.quoteItems.count) || store[MODULE_KEY]?.isLoading;

/**
 * Determines if the payment is fulfilled
 *
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} true if the payment status is fulfilled
 */
export const isPaymentsInStore = (store: ApplicationStoreType): boolean =>
    !!store?.payments?.amazon;

/**
 * Get count of items of shopping cart
 * @param {ApplicationStoreType} store - Application store
 * @return {number} return items count
 */
export const getShoppingCartItemsCount = createSelector(getQuoteItems, items => {
    return items.filter(({productAsHto}) => !productAsHto).length;
});
